import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Building2, ChevronDownIcon } from 'lucide-react'

import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from '@/components/ui/combobox'
import { PopoverTrigger } from '@/components/ui/popover'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { AdvertisersApi } from '@/services/advertisers/advertisersApi'

/** Stands in for "any advertiser" — cmdk items need a non-empty value. */
const ANY = '__any__'

/** Shared key, so every picker reads one cached advertiser list. */
export const ADVERTISERS_QUERY_KEY = ['advertisers']

export interface AdvertiserSelectProps {
  /** Advertiser id, or '' for no selection. */
  value: string
  onChange: (advertiserId: string) => void
  /** Label for the empty choice. Omit to leave the choice out. */
  anyLabel?: string
  disabled?: boolean
  className?: string
}

/**
 * Searchable advertiser picker. Used both as a form field and — with
 * `anyLabel` — as a list filter, where '' means "not filtered".
 */
export function AdvertiserSelect({
  value,
  onChange,
  anyLabel,
  disabled,
  className,
}: AdvertiserSelectProps) {
  const { t } = useTranslation()

  const { data: advertisers = [] } = useQuery({
    queryKey: ADVERTISERS_QUERY_KEY,
    queryFn: () => AdvertisersApi.getAll(),
    staleTime: 5 * 60 * 1000,
  })

  const selected = advertisers.find((a) => a.id === value)
  const label = selected?.name ?? (value || anyLabel || t('advertisers.selectAdvertiser'))

  return (
    <Combobox
      value={value}
      onValueChange={(v) => onChange(v === ANY ? '' : v)}
      disabled={disabled}
    >
      {/* The combobox root renders the popover, so the trigger is the button
          itself — styled to match the select triggers it sits beside. */}
      <PopoverTrigger asChild>
        <Button
          variant="field"
          size="sm"
          disabled={disabled}
          className={cn('h-8 w-full justify-between px-3', className)}
        >
          {/* "Any" is a real choice, so only a missing selection reads as a placeholder. */}
          <span className={cn('truncate', !value && !anyLabel && 'text-muted-foreground')}>{label}</span>
          <ChevronDownIcon className="size-4 shrink-0 text-muted-foreground" />
        </Button>
      </PopoverTrigger>
      <ComboboxContent className="w-64">
        <ComboboxInput placeholder={t('advertisers.searchAdvertisers')} />
        <ComboboxList>
          <ComboboxEmpty>{t('advertisers.noAdvertisersFound')}</ComboboxEmpty>
          {anyLabel && (
            <ComboboxItem value={ANY} keywords={[anyLabel]}>
              <span className="text-muted-foreground">{anyLabel}</span>
            </ComboboxItem>
          )}
          {advertisers.map((advertiser) => (
            <ComboboxItem key={advertiser.id} value={advertiser.id} keywords={[advertiser.name]}>
              <Building2 className="size-4 shrink-0 text-muted-foreground" />
              <span className="truncate">{advertiser.name}</span>
            </ComboboxItem>
          ))}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  )
}
